import { PropertyRow, ValueType, PropertyValue } from './types';

/**
 * Infer the type from a value
 */
export const inferTypeFromValue = (value: any): ValueType => {
  if (typeof value === 'number' && !isNaN(value)) return 'number';
  if (typeof value === 'boolean') return 'boolean';
  return 'string';
};

/**
 * Coerce a value to the specified type
 */
export const coerceToType = (value: any, targetType: ValueType): PropertyValue => {
  switch (targetType) {
    case 'number':
      if (typeof value === 'number') return value;
      if (typeof value === 'string') {
        const parsed = parseFloat(value);
        return isNaN(parsed) ? 0 : parsed;
      }
      if (typeof value === 'boolean') return value ? 1 : 0;
      return 0;

    case 'boolean':
      if (typeof value === 'boolean') return value;
      if (typeof value === 'string') {
        const lower = value.toLowerCase().trim();
        return lower === 'true' || lower === '1' || lower === 'yes';
      }
      if (typeof value === 'number') return value !== 0;
      return false;

    case 'string':
    default:
      if (value === null || value === undefined) return '';
      if (typeof value === 'object') return JSON.stringify(value);
      return String(value);
  }
};

/**
 * Normalize a key by trimming whitespace
 */
export const normalizeKey = (key: string): string => {
  return key.trim();
};

/**
 * Check if a key is duplicate in the rows
 */
export const hasDuplicateKey = (key: string, currentId: string, rows: PropertyRow[]): boolean => {
  const normalizedKey = normalizeKey(key);
  if (!normalizedKey) return false;

  return rows.some((row) => row.id !== currentId && normalizeKey(row.key) === normalizedKey);
};

/**
 * Generate a unique ID for rows
 */
export const generateRowId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
};

/**
 * Convert initial entries to PropertyRow format
 */
export const buildRowsFromInitialEntries = (
  initialEntries: Array<{ id: number; propertyKey: string; value: string | number }> = []
): PropertyRow[] => {
  return initialEntries.map((entry) => ({
    id: generateRowId(),
    key: entry.propertyKey,
    type: inferTypeFromValue(entry.value),
    value: entry.value
  }));
};

/**
 * Convert PropertyRow array back to the expected format
 */
export const buildEntriesFromRows = (
  rows: PropertyRow[]
): Array<{ id: number; propertyKey: string; value: string | number }> => {
  return rows
    .filter((row) => normalizeKey(row.key) && !row.keyError && !row.valueError)
    .map((row, index) => ({
      id: Date.now() + index, // Generate numeric id for compatibility
      propertyKey: normalizeKey(row.key),
      value: row.type === 'string' ? String(row.value || '') : (row.value as string | number)
    }));
};

/**
 * Validate a single row
 */
export const validateRow = (row: PropertyRow, allRows: PropertyRow[]): PropertyRow => {
  const errors: Partial<Pick<PropertyRow, 'keyError' | 'valueError'>> = {};

  // Validate key
  const normalizedKey = normalizeKey(row.key);
  if (!normalizedKey) {
    errors.keyError = 'Key is required';
  } else if (hasDuplicateKey(row.key, row.id, allRows)) {
    errors.keyError = 'Key must be unique';
  }

  // Validate value for numbers
  if (row.type === 'number' && typeof row.value === 'string') {
    const parsed = parseFloat(row.value);
    if (isNaN(parsed)) {
      errors.valueError = 'Invalid number';
    }
  }

  return { ...row, ...errors };
};

/**
 * Validate all rows
 */
export const validateRows = (rows: PropertyRow[]): PropertyRow[] => {
  return rows.map((row) => validateRow(row, rows));
};
